import { NodeKey } from "../types"
import { BackendActionType, BackendRecordType, BackendTriggerType } from "./automation-transformer.types"

export const MAX_WAIT_TIME_SECONDS = 30 * 24 * 60 * 60

// Map frontend trigger node keys to backend trigger types
export const TRIGGER_TYPE_MAP: Record<string, BackendTriggerType> = {
  trigger__service_request_completed: "AutomationTriggers::ServiceRequestCompleted",
  trigger__form_accepted: "AutomationTriggers::FormAccepted",
  trigger__contact_updated: "AutomationTriggers::AttributeUpdated",
  trigger__contact_created: "AutomationTriggers::RecordCreated",
  trigger__contact_tag_added: "AutomationTriggers::TagAdded",
  trigger__contact_sequence_completed: "AutomationTriggers::SequenceCompleted",
  trigger__contact_magic_button: "AutomationTriggers::MagicButton",
  trigger__business_created: "AutomationTriggers::RecordCreated",
  trigger__business_updated: "AutomationTriggers::AttributeUpdated",
  trigger__business_tag_added: "AutomationTriggers::TagAdded",
  trigger__business_magic_button: "AutomationTriggers::MagicButton",
  trigger__household_created: "AutomationTriggers::RecordCreated",
  trigger__household_updated: "AutomationTriggers::AttributeUpdated",
  trigger__client_touchpoint_reminder: "AutomationTriggers::DateReminder",
  trigger__household_tag_added: "AutomationTriggers::TagAdded",
  trigger__household_sequence_completed: "AutomationTriggers::SequenceCompleted",
  trigger__prospect_promoted_to_client: "AutomationTriggers::ProspectPromoted",
  trigger__client_archived_to_past_client: "AutomationTriggers::ClientArchived",
  trigger__household_magic_button: "AutomationTriggers::MagicButton",
  trigger__task_created: "AutomationTriggers::RecordCreated",
  trigger__task_completed: "AutomationTriggers::AttributeUpdated",
  trigger__meeting_scheduled: "AutomationTriggers::MeetingScheduled",
  trigger__meeting_completed: "AutomationTriggers::MeetingCompleted",
  trigger__meeting_magic_button: "AutomationTriggers::MagicButton",
  trigger__opportunity_created: "AutomationTriggers::RecordCreated",
  trigger__opportunity_stage_moved: "AutomationTriggers::AttributeUpdated",
  trigger__project_created: "AutomationTriggers::RecordCreated",
  trigger__project_milestone_changed: "AutomationTriggers::AttributeUpdated",
  trigger__project_completed: "AutomationTriggers::AttributeUpdated",
  trigger__email_received: "AutomationTriggers::EmailReceived",
  trigger__recurring: "AutomationTriggers::Recurring",
  trigger__birthday_reminder: "AutomationTriggers::DateReminder",
  trigger__age_milestone_reminder: "AutomationTriggers::DateReminder",
  trigger__annual_review_reminder: "AutomationTriggers::DateReminder",
  trigger__client_anniversary_reminder: "AutomationTriggers::DateReminder",
  trigger__custom_date_reminder: "AutomationTriggers::DateReminder",
  trigger__meeting_reminder: "AutomationTriggers::MeetingReminder",
}

// Map frontend action node keys to backend action types
export const ACTION_TYPE_MAP: Record<string, BackendActionType> = {
  // AI actions
  action__ai_categorize: "AutomationAction::AI::Categorize",
  action__ai_draft_email: "AutomationAction::AI::DraftEmail",
  action__ai_filter: "AutomationAction::AI::Filter",
  action__ai_parse_and_create_contact: "AutomationAction::AI::ParseAndCreateContact",
  action__ai_research: "AutomationAction::AI::Research",
  // Flow actions
  action__flow_branch: "AutomationAction::Flow::Branch",
  action__flow_wait: "AutomationAction::Shared::Wait",
  // Contact actions
  action__contact_add_tag: "AutomationAction::Contact::AddTag",
  action__contact_remove_tag: "AutomationAction::Contact::RemoveTag",
  action__contact_add_note: "AutomationAction::Contact::AddNote",
  action__contact_add_to_sequence: "AutomationAction::Contact::AddToSequence",
  action__contact_remove_from_sequence: "AutomationAction::Contact::RemoveFromSequence",
  action__contact_send_email: "AutomationAction::Contact::SendEmail",
  action__contact_send_sms: "AutomationAction::Contact::SendSMS",
  // Household actions
  action__add_note: "AutomationAction::Household::AddNote",
  action__add_tag: "AutomationAction::Household::AddTag",
  action__remove_tag: "AutomationAction::Household::RemoveTag",
  action__add_to_sequence: "AutomationAction::Household::AddToSequence",
  action__create_opportunity: "AutomationAction::Household::CreateOpportunity",
  action__find_project: "AutomationAction::Household::FindProject",
  action__find_opportunity: "AutomationAction::Household::FindOpportunity",
  action__household_add_comment: "AutomationAction::Record::AddComment",
  action__household_send_email: "AutomationAction::Household::SendEmail",
  action__household_send_sms: "AutomationAction::Household::SendSMS",
  action__household_delete_open_tasks: "AutomationAction::Household::DeleteOpenTasks",
  action__promote_to_client: "AutomationAction::Household::PromoteToClient",
  action__remove_from_sequence: "AutomationAction::Household::RemoveFromSequence",
  action__start_project: "AutomationAction::Household::StartProject",
  action__business_start_project: "AutomationAction::Business::StartProject",
  action__business_add_note: "AutomationAction::Business::AddNote",
  action__business_send_email: "AutomationAction::Business::SendEmail",
  action__business_send_sms: "AutomationAction::Business::SendSMS",
  action__send_intake_form: "AutomationAction::Household::SendIntakeForm",
  action__business_update_attribute: "AutomationAction::Record::UpdateAttribute",
  action__contact_update_attribute: "AutomationAction::Record::UpdateAttribute",
  action__update_attribute: "AutomationAction::Household::UpdateAttribute",
  // Opportunity actions
  action__opportunity_add_comment: "AutomationAction::Record::AddComment",
  action__opportunity_move_stage: "AutomationAction::Opportunity::MoveStage",
  // Project actions
  action__project_move_milestone: "AutomationAction::Project::MoveMilestone",
  // Record actions
  action__record_create_task: "AutomationAction::Record::CreateTask",
  // System actions
  action__confetti: "AutomationAction::System::Confetti",
  action__internal_notification: "AutomationAction::System::InternalNotification",
  action__search_records: "AutomationAction::System::SearchRecord",
  action__user_action: "AutomationAction::System::UserAction",
  // Task actions
  action__task_add_comment: "AutomationAction::Record::AddComment",
  action__update_task: "AutomationAction::Task::UpdateTask",
  action__update_tasks: "AutomationAction::Task::UpdateTask",
}

// Extract record type from trigger node key
export function extractRecordTypeFromNodeKey(nodeKey: NodeKey): BackendRecordType | null {
  if (nodeKey === "trigger__service_request_completed") return "service_request"
  if (nodeKey.startsWith("trigger__contact_")) return "contact"
  if (nodeKey.startsWith("trigger__business_")) return "business"
  if (nodeKey.includes("task")) return "task"
  if (nodeKey.includes("opportunity")) return "opportunity"
  if (nodeKey.includes("project")) return "project"
  if (nodeKey.includes("meeting")) return "meeting"
  if (nodeKey.includes("email")) return "email_message"
  if (nodeKey === "trigger__recurring") return null // System trigger, no record type
  if (nodeKey === "trigger__prospect_promoted_to_client") return "client"
  if (nodeKey === "trigger__client_archived_to_past_client") return "past_client"
  if (nodeKey === "trigger__birthday_reminder") return "person"
  if (nodeKey === "trigger__age_milestone_reminder") return "person"
  if (nodeKey === "trigger__annual_review_reminder") return "client"
  if (nodeKey === "trigger__client_anniversary_reminder") return "client"
  if (nodeKey === "trigger__client_touchpoint_reminder") return "client"

  // Household types need special handling
  return null
}

// Resolve household type to backend record type
export function resolveHouseholdType(householdType: string | undefined): BackendRecordType {
  if (!householdType) {
    // Default to client if not specified
    return "client"
  }

  const normalized = householdType.toLowerCase()
  if (normalized === "prospect") return "prospect"
  if (normalized === "client") return "client"
  if (normalized === "household") return "household"

  // Default fallback
  return "client"
}

// Transform wait period to seconds
export function convertWaitPeriodToSeconds(value: string | number, period: string): number {
  const numValue = typeof value === "string" ? parseInt(value, 10) : value

  switch (period) {
    case "seconds":
      return numValue
    case "minutes":
      return numValue * 60
    case "hours":
      return numValue * 60 * 60
    case "days":
      return numValue * 60 * 60 * 24
    case "weeks":
      return numValue * 60 * 60 * 24 * 7
    default:
      return numValue
  }
}

// Transform task priority from frontend to backend format
// Backend enum: { low: 0, medium: 1, high: 2, urgent: 3 }
export function transformTaskRepeat(repeat: string | undefined | null): {
  interval_value: number | null
  interval_unit: "days" | "weeks" | "months" | "years" | null
} {
  const [rawValue, rawUnit] = (repeat ?? "").split(":")
  const intervalValue = Number(rawValue)
  if (!Number.isInteger(intervalValue) || intervalValue < 1 || intervalValue > 12) {
    return { interval_value: null, interval_unit: null }
  }
  if (rawUnit !== "days" && rawUnit !== "weeks" && rawUnit !== "months" && rawUnit !== "years") {
    return { interval_value: null, interval_unit: null }
  }

  return { interval_value: intervalValue, interval_unit: rawUnit }
}

export function transformTaskPriority(priority: string | undefined): number | undefined {
  if (!priority || priority === "none") return undefined

  const priorityMap: Record<string, number> = {
    low: 0,
    medium: 1,
    high: 2,
    urgent: 3,
  }

  return priorityMap[priority.toLowerCase()]
}

// Backend attribute names mapped from frontend field names
export const ATTRIBUTE_NAME_MAP: Record<string, Record<string, string>> = {
  household: {
    tier: "tier",
    aum: "aum",
    name: "name",
  },
  task: {
    status: "status",
  },
  opportunity: {
    pipeline_stage: "pipeline_stage_id",
    value: "value",
    probability: "probability",
  },
  project: {
    status: "status",
    milestone: "project_milestone_id",
    assigned_to: "assigned_to_id",
  },
}

// Transform frontend attribute names to backend format
export function transformAttributeNames(attributes: string[] | undefined, recordType: string): string[] {
  if (!attributes || attributes.length === 0) return []

  const mapping = ATTRIBUTE_NAME_MAP[recordType] || {}
  return attributes.map((attr) => mapping[attr] || attr)
}

// Maps record type to valid condition entity_types for that trigger context.
// Used to filter out stale conditions left over from a previous trigger type.
export const VALID_CONDITION_ENTITY_TYPES: Record<string, string[]> = {
  service_request: [],
  intake_form_submission: ["intake_form_submission", "household", "client", "prospect", "person"],
  task: ["task"],
  opportunity: ["opportunity"],
  project: ["project"],
  meeting: ["meeting"],
  client: ["client"],
  past_client: ["client"],
  prospect: ["client"],
  household: ["client"],
  person: ["person"],
  contact: ["contact"],
  business: ["business"],
  email_message: ["automation_email_message"],
  automation_email_message: ["automation_email_message"],
}

// Validate that a trigger type supports the given record type
export function validateTriggerRecordType(
  triggerType: BackendTriggerType,
  recordType: BackendRecordType | null
): boolean {
  // Map of trigger types to their allowed record types
  const allowedTypes: Record<BackendTriggerType, (BackendRecordType | null)[]> = {
    "AutomationTriggers::ServiceRequestCompleted": ["service_request"],
    "AutomationTriggers::RecordCreated": [
      "client",
      "prospect",
      "contact",
      "business",
      "task",
      "opportunity",
      "project",
    ],
    "AutomationTriggers::AttributeUpdated": [
      "contact",
      "client",
      "prospect",
      "household",
      "business",
      "task",
      "opportunity",
      "project",
    ],
    "AutomationTriggers::TagAdded": ["household", "contact", "business"],
    "AutomationTriggers::SequenceCompleted": ["client", "prospect", "contact"],
    "AutomationTriggers::RecordingFinished": ["meeting"],
    "AutomationTriggers::EmailReceived": ["automation_email_message"],
    "AutomationTriggers::FormAccepted": ["intake_form_submission"],
    "AutomationTriggers::MagicButton": ["client", "prospect", "household", "contact", "business", "meeting"],
    "AutomationTriggers::MeetingCompleted": ["meeting"],
    "AutomationTriggers::MeetingScheduled": ["meeting"],
    "AutomationTriggers::ProspectPromoted": ["client"],
    "AutomationTriggers::ClientArchived": ["past_client"],
    "AutomationTriggers::Recurring": [null], // No record type for recurring triggers
    "AutomationTriggers::DateReminder": ["person", "client", "prospect"],
    "AutomationTriggers::MeetingReminder": ["meeting"],
  }

  const allowed = allowedTypes[triggerType]
  if (!allowed) return false

  return allowed.includes(recordType)
}
